import React, { FC, ReactElement } from 'react';
import { StyleProp, Text, View, ViewStyle } from 'react-native';
import { ScaleFunction } from '@components/operatorPanel/interfaces';
import { font as initialFont } from '@styles/fonts';
import { scale as initialScale } from '@components/frame';
import { ALL_JOBS_ID, SCREEN_PADDING_HORIZONTAL_FOR_ITEM } from '@v2/pages/common/constants/base.constants';
import { IMAGE_HANDLING_HEADER_HEIGHT } from '@components/imageHandling/common';
import ColorWrapper, { WithColor } from '@styles/colors';
import { OperationSearchItem, ProductionOrderSearchItem } from '@interfaces/search';
import { JOB_STATUSES } from '@helpers';
import { PaginatedResponse } from '@interfaces/machines';
import CloseButton from '@components/closeButton';
import ProductionOrderContent from '@v2/pages/productionOrder/modules/productionOrder/components/ProductionOrderContent';
import ProductionOrderJobsContent from '@v2/pages/productionOrder/modules/productionOrder/components/ProductionOrderJobsContent';
import { SingleJobOrList } from '@v2/pages/machines/modules/now/components/Job/components/SingleJobOrList';
import DetailsPageWrapper from '@v2/pages/common/components/DetailsPageWrapper/DetailsPageWrapper';
import SendToImnooButton from '@v2/pages/productionOrder/modules/productionOrder/components/SendToImnoo';
import { useManageProductionOrderContentDifferentModes } from './hooks/useManageProductionOrderContentDifferentModes';

export interface ProductionOrderContentDifferentModesProps extends WithColor {
  scale?: ScaleFunction;
  font?: typeof initialFont;
  productionOrder: ProductionOrderSearchItem;
  maxContentWidth: number;
  jobsApiCall: (() => Promise<PaginatedResponse<OperationSearchItem>>) | null;
  isError: boolean;
  loading: boolean;
  totalCount: number;
  bg?: string;
  withTitle?: boolean;
  withoutHeader?: boolean;
  displaySingleJobWithoutNavigation?: boolean;
  contentStyle?: StyleProp<ViewStyle>;
  closeButtonStyle?: StyleProp<ViewStyle>;
  singleJobHeaderContainerStyle?: StyleProp<ViewStyle>;
}

const ProductionOrderContentDifferentModes: FC<ProductionOrderContentDifferentModesProps> = ({
  C,
  productionOrder,
  loading,
  maxContentWidth,
  totalCount,
  isError,
  jobsApiCall,
  bg,
  contentStyle,
  closeButtonStyle,
  singleJobHeaderContainerStyle,
  withoutHeader = false,
  withTitle = false,
  displaySingleJobWithoutNavigation = false,
  font = initialFont,
  scale = initialScale,
}) => {
  const { selectedItem, jobs, title, jobId, onClose } = useManageProductionOrderContentDifferentModes();

  const CloseButtonComponent = (
    <View
      style={[
        {
          flexShrink: 0,
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'flex-end',
          marginRight: scale(6),
          gap: scale(22),
        },
        closeButtonStyle,
      ]}
    >
      {productionOrder?.status === JOB_STATUSES.FINISHED ? <SendToImnooButton scale={scale} font={font} /> : null}
      <CloseButton C={C} onPress={onClose} scale={scale} />
    </View>
  );

  const renderContent = () => {
    let content = <React.Fragment></React.Fragment>;

    const getMultiJobsWrapper = (content: ReactElement) => (
      <View style={{ paddingHorizontal: scale(18 - SCREEN_PADDING_HORIZONTAL_FOR_ITEM), flex: 1 }}>
        <View
          style={{
            flexDirection: 'row',
            justifyContent: 'space-between',
            width: '100%',
            alignItems: 'center',
            height: scale(IMAGE_HANDLING_HEADER_HEIGHT),
          }}
        >
          <Text style={[font.Headline, { color: C('Label/Light/Primary'), paddingHorizontal: scale(15), paddingVertical: scale(19) }]}>
            {productionOrder?.productionOrderNumber}
          </Text>
          {CloseButtonComponent}
        </View>
        {content}
      </View>
    );

    // Production Order Tab Selected
    if (jobId == null) {
      content = <ProductionOrderContent {...{ maxWidth: maxContentWidth, loading, productionOrder, bg, scale, font }} />;

      return !withTitle ? content : getMultiJobsWrapper(content);
    }

    // All Jobs Tab Selected
    if (jobId === ALL_JOBS_ID) {
      content = <ProductionOrderJobsContent {...{ totalCount, isError, C, maxContentWidth, jobsApiCall, font, scale }} />;

      return !withTitle ? content : getMultiJobsWrapper(content);
    }

    // Specific Job Selected
    if (displaySingleJobWithoutNavigation) {
      const selectedJob = jobs.data?.find((j) => j.productionOperationId === +jobId);
      if (!selectedJob) return null;

      const content = (
        <SingleJobOrList
          jobs={[selectedJob]}
          jobId={jobId}
          loading={!jobs.wasLoaded}
          displayImagesColumn={false}
          ignoreHeaderConfig
          displaySingleJob
          maxDetailsWidth={maxContentWidth}
          scale={scale}
          font={font}
        />
      );

      if (!withTitle) {
        return content;
      }

      return (
        <View style={{ paddingHorizontal: scale(18 - SCREEN_PADDING_HORIZONTAL_FOR_ITEM), flex: 1 }}>
          <View style={{ flexDirection: 'row', justifyContent: 'space-between', width: '100%', alignItems: 'center' }}>
            <View style={[{ flexDirection: 'row', paddingHorizontal: scale(15), paddingVertical: scale(19), flex: 1 }, singleJobHeaderContainerStyle]}>
              <Text numberOfLines={1} style={{ color: C('Label/Light/Tertiary') }}>
                <Text style={[font.Headline, { color: C('Label/Light/Primary') }]}>{selectedJob.productionOperationNumber} </Text>
                <Text style={[font.Headline, { color: C('Label/Light/Tertiary') }]}>{selectedJob.operationName}</Text>
              </Text>
            </View>
            {CloseButtonComponent}
          </View>

          {content}
        </View>
      );
    }

    return null;
  };

  const innerContent = <View style={[{ flex: 1 }, contentStyle]}>{renderContent()}</View>;

  return withoutHeader ? (
    innerContent
  ) : (
    <DetailsPageWrapper title={title} shouldUsePhoneLayoutForTitle>
      {innerContent}
    </DetailsPageWrapper>
  );
};

export default ColorWrapper(ProductionOrderContentDifferentModes);
